import { Country } from "@/components/CountryAutoComplete";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { logout, loginSuccess } from "../auth/authSlice";

type FilterValues = {
  searchType?: string[];
  searchValue?: Record<string, string>;
  date_range?: string | null;
  from_date?: Date | string | null;
  to_date?: Date | string | null;
  dateType?: string;
  products?: string[] | null;
  country_of_incidence?: string | null | Country;
  date_type?: string | null;
  case_number?: string | null;
  case_reference?: string | null;
  event_term?: string | null;
  event_verbatim?: string | null;
  study_id?: string | null;
  study_name?: string | null;
  project_id?: string | null;
  studyTypes?: string[];
  studyValue?: Record<string, string>;
};

type AdvancedFilterValues = {
  reportType: string;
  lotNumber: string;
  workflowState: string;
  caseStatus: string;
  caseSeriousness: string;
  customSQL: string;
};

interface FilterState {
  filterParams: FilterValues;
  advancedFilters: AdvancedFilterValues;
  isFilterOpen: boolean;
  selectedSearchFilter: string | undefined | null;
  isSaveOperationInProgress: boolean;
}

const initialState: FilterState = {
  selectedSearchFilter: "",
  filterParams: {
    searchType: [],
    searchValue: {},
    date_range: null,
    from_date: null,
    to_date: null,
    date_type: null,
    products: null,
    country_of_incidence: null,
    studyTypes: [],
    studyValue: {},
  },
  advancedFilters: {
    reportType: "",
    lotNumber: "",
    workflowState: "",
    caseStatus: "",
    caseSeriousness: "",
    customSQL: "",
  },
  isFilterOpen: false,
  isSaveOperationInProgress: false,
};

export const filterSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    setSelectedSearch: (state, action: PayloadAction<string>) => {
      state.selectedSearchFilter = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<FilterValues>>) => {
      state.filterParams = { ...state.filterParams, ...action.payload };
    },
    setSearchType: (state, action: PayloadAction<string[]>) => {
      if (action.payload[0] === "case_number") {
        state.filterParams = {
          date_range: null,
          from_date: null,
          to_date: null,
          date_type: null,
          products: null,
          country_of_incidence: null,
        };
      }
      state.filterParams.searchType = action.payload;
    },
    setSearchValue: (state, action: PayloadAction<Record<string, string>>) => {
      state.filterParams.searchValue = action.payload;
    },
    setDateRange: (state, action: PayloadAction<string | null>) => {
      state.filterParams.date_range = action.payload;
    },
    setFromDate: (state, action: PayloadAction<Date | string | null>) => {
      state.filterParams.from_date = action.payload;
    },
    setToDate: (state, action: PayloadAction<Date | string | null>) => {
      state.filterParams.to_date = action.payload;
    },
    setDateType: (state, action: PayloadAction<string>) => {
      state.filterParams.date_type = action.payload;
    },
    setProducts: (state, action: PayloadAction<string[] | null>) => {
      state.filterParams.products = action.payload;
    },
    setCountryOfIncidence: (state, action: PayloadAction<string | null | Country>) => {
      state.filterParams.country_of_incidence = action.payload;
    },
    setStudyTypes: (state, action: PayloadAction<string[]>) => {
      state.filterParams.studyTypes = action.payload;
    },
    setStudyValue: (state, action: PayloadAction<Record<string, string>>) => {
      state.filterParams.studyValue = action.payload;
    },
    setDateRangeWithDates: (
      state,
      action: PayloadAction<{
        range: string;
        fromDate: Date | string | null;
        toDate: Date | string | null;
      }>
    ) => {
      state.filterParams.date_range = action.payload.range;
      state.filterParams.from_date = action.payload.fromDate;
      state.filterParams.to_date = action.payload.toDate;
    },
    resetFilters: (state) => {
      state.filterParams = {
        searchType: [],
        searchValue: {},
        date_range: null,
        from_date: null,
        to_date: null,
        date_type: null,
        products: null,
        country_of_incidence: null,
        studyTypes: [],
        studyValue: {},
      };
    },
    setFilterOpen: (state, action: PayloadAction<boolean>) => {
      state.isFilterOpen = action.payload;
    },
    setAdvancedFilters: (
      state,
      action: PayloadAction<AdvancedFilterValues>
    ) => {
      state.advancedFilters = action.payload;
    },
    resetAdvancedFilters: (state) => {
      state.advancedFilters = {
        reportType: "",
        lotNumber: "",
        workflowState: "",
        caseStatus: "",
        caseSeriousness: "",
        customSQL: "",
      };
    },
    setSaveOperationInProgress: (state, action: PayloadAction<boolean>) => {
      state.isSaveOperationInProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Reset filter state when user logs out
    builder.addCase(logout, () => {
      return initialState;
    });
    // Reset filter state when a new user logs in (to prevent filter leakage between users)
    builder.addCase(loginSuccess, () => {
      return initialState;
    });
  },
});

export const {
  setFilters,
  setSearchType,
  setSearchValue,
  setDateRange,
  setFromDate,
  setToDate,
  setDateType,
  setProducts,
  setCountryOfIncidence,
  setStudyTypes,
  setStudyValue,
  setDateRangeWithDates,
  resetFilters,
  setFilterOpen,
  setAdvancedFilters,
  resetAdvancedFilters,
  setSelectedSearch,
  setSaveOperationInProgress
} = filterSlice.actions;

export default filterSlice.reducer;
